import React, { useState, useEffect } from 'react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { getPromptVersions } from '../../../../../services/aiOperationsApi';

const ENV_COLORS: Record<string, string> = {
  development: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  staging: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  production: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

const getEnvColor = (env: string): string =>
  ENV_COLORS[env] ?? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';

interface PromptVersion {
  id: string;
  version_number: number;
  is_latest?: boolean;
  description?: string;
  created_at: string;
}

interface PromptItem {
  id: string;
  name: string;
  environment?: string;
  version_number?: number;
  is_latest?: boolean;
}

interface RollbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: PromptItem | null;
  onRollback?: (promptId: string, versionNumber: number) => void;
  isLoading?: boolean;
}

export const RollbackModal: React.FC<RollbackModalProps> = ({
  isOpen,
  onClose,
  prompt,
  onRollback,
  isLoading = false,
}) => {
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && prompt) {
      fetchVersions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prompt]);

  const fetchVersions = async () => {
    if (!prompt) return;
    setLoadingVersions(true);
    setError(null);
    try {
      const currentEnv = prompt.environment ?? 'development';
      const response = await getPromptVersions(prompt.id, currentEnv);
      const otherVersions = (response.versions ?? []).filter(
        (v: PromptVersion) => v.version_number !== prompt.version_number
      );
      setVersions(otherVersions);
    } catch (err: unknown) {
      console.error('Failed to fetch versions:', err);
      setError('Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRollback = () => {
    if (selectedVersion != null && onRollback && prompt) {
      onRollback(prompt.id, selectedVersion);
    }
  };

  const handleClose = () => {
    setSelectedVersion(null);
    setVersions([]);
    setError(null);
    onClose();
  };

  if (!prompt) return null;

  const currentEnv = prompt.environment ?? 'development';

  let versionListContent: React.ReactElement;
  if (loadingVersions) {
    versionListContent = (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin h-6 w-6 text-blue-500" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  } else if (error) {
    versionListContent = (
      <div className="text-center py-6 text-red-500 dark:text-red-400">
        {error}
      </div>
    );
  } else if (versions.length === 0) {
    versionListContent = (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
        <svg className="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p>No previous versions available in {currentEnv}</p>
      </div>
    );
  } else {
    versionListContent = (
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {versions.map((version) => (
          <button
            key={version.id}
            onClick={() => setSelectedVersion(version.version_number)}
            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
              selectedVersion === version.version_number
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-white">
                  Version {version.version_number}
                </span>
                {version.is_latest && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    was latest
                  </span>
                )}
              </div>
              {selectedVersion === version.version_number && (
                <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {version.description ?? 'No description'}
            </div>
            <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Created: {new Date(version.created_at).toLocaleDateString()}
            </div>
          </button>
        ))}
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Rollback Prompt" maxWidth="max-w-lg">
      <div className="space-y-6">
        {/* Current Prompt Info */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
            {prompt.name}
          </h4>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Current:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getEnvColor(currentEnv)}`}>
              {currentEnv}
            </span>
            <span className="text-gray-400 dark:text-gray-500">v{prompt.version_number ?? 1}</span>
            {prompt.is_latest && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                latest
              </span>
            )}
          </div>
        </div>

        {/* Version List */}
        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Select version to rollback to
          </span>

          {versionListContent}
        </div>

        {/* Warning */}
        {selectedVersion != null && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-orange-700 dark:text-orange-300">
                <p className="font-medium">Version {selectedVersion} will become the active version</p>
                <p className="mt-1 text-orange-600 dark:text-orange-400">
                  Agents fetching this prompt in {currentEnv} will receive version {selectedVersion}.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRollback} disabled={isLoading || selectedVersion === null}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Rolling back...
              </span>
            ) : (
              `Rollback to v${selectedVersion}`
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RollbackModal;
